class FreeShippingMeter extends HTMLElement {
    static classLabel1 = 'progress-30';
    static classLabel2 = 'progress-60';
    static classLabel3 = 'progress-100';

    constructor() {
        super();
        this.onCartChange = this.onCartChange.bind(this);
    }

    connectedCallback() {
        this.progressBar = this.querySelector('[data-shipping-progress]');
        this.progressTrack = this.querySelector('.progress_shipping');
        this.progressMeter = this.querySelector('[data-free-shipping-progress-meter]');
        this.messageElement = this.querySelector('[data-shipping-message]');
        this.textEnabled = this.progressBar?.dataset.textEnabled === 'true';

        this.addEventListener('change', this.onCartChange);
        this.initialize();
    }

    disconnectedCallback() {
        this.removeEventListener('change', this.onCartChange);
    }

    initialize() {
        Shopify.getCart((cart) => {
            this.cart = cart;
            this.render(cart);
        });
    }

    onCartChange() {
        this.initialize();
    }

    get config() {
        return window.cart_goal_settings || {};
    }

    parseNumber(value) {
        if (typeof value === 'number') return value;
        if (typeof value !== 'string') return 0;

        const normalized = value.replace(',', '.').replace(/[^\d.-]/g, '');
        const parsed = parseFloat(normalized);

        return Number.isFinite(parsed) ? parsed : 0;
    }

    toCents(value) {
        const amount = this.parseNumber(value);
        return amount > 0 ? Math.round(amount * 100) : 0;
    }

    formatMoney(cents) {
        return Shopify.formatMoney(cents, window.money_format);
    }

    formatTemplate(template, data = {}) {
        return (template || '').replace(/\[(amount|reward|threshold)\]/gi, (match, key) => {
            const normalizedKey = key.toLowerCase();
            return data[normalizedKey] ?? '';
        });
    }

    getFreeShippingTexts() {
        return window.free_shipping_text || {};
    }

    isShippingDeliveryMode() {
        return this.config.calculationMode === 'setup_shipping_delivery' || document.body.classList.contains('setup_shipping_delivery');
    }

    getItemBaseLinePrice(item) {
        const originalLinePrice = Number(item?.original_line_price);
        if (Number.isFinite(originalLinePrice) && originalLinePrice > 0) {
            return originalLinePrice;
        }

        const originalPrice = Number(item?.original_price);
        const quantity = Number(item?.quantity || 0);
        if (Number.isFinite(originalPrice) && Number.isFinite(quantity) && originalPrice > 0 && quantity > 0) {
            return originalPrice * quantity;
        }

        const finalLinePrice = Number(item?.final_line_price);
        if (Number.isFinite(finalLinePrice) && finalLinePrice > 0) {
            return finalLinePrice;
        }

        const linePrice = Number(item?.line_price);
        return Number.isFinite(linePrice) && linePrice > 0 ? linePrice : 0;
    }

    getEligibleSubtotal(cart) {
        const items = Array.isArray(cart?.items) ? cart.items : [];
        const excludeGiftCards = this.isShippingDeliveryMode();

        if (items.length) {
            const subtotal = items.reduce((sum, item) => {
                if (excludeGiftCards && item?.gift_card) {
                    return sum;
                }

                return sum + this.getItemBaseLinePrice(item);
            }, 0);

            return Math.max(0, subtotal);
        }

        let subtotal = Number(cart?.original_total_price);

        if (!Number.isFinite(subtotal) || subtotal < 0) {
            subtotal = Number(cart?.items_subtotal_price);
        }

        if (!Number.isFinite(subtotal) || subtotal < 0) {
            subtotal = Number(cart?.total_price || 0);
        }

        if (excludeGiftCards) {
            const giftCardItems = document.querySelectorAll('.cart-item[data-price-gift-card], .previewCartItem[data-price-gift-card]');

            giftCardItems.forEach((item) => {
                subtotal -= this.parseNumber(item.getAttribute('data-price-gift-card'));
            });
        }

        return Math.max(0, subtotal);
    }

    getMode() {
        return this.config.mode || this.dataset.goalMode || 'free_shipping';
    }

    getFreeShippingThreshold() {
        const configThreshold = this.config.freeShippingThreshold;
        const fallbackThreshold = window.free_shipping_price;
        return this.toCents(configThreshold || fallbackThreshold);
    }

    getSingleDiscountThreshold() {
        return this.toCents(this.config.discountThreshold);
    }

    getConfiguredMaxDiscountPercent() {
        const mode = this.getMode();

        if (mode === 'discount') {
            return this.extractPercentValue(this.config.discountReward || '');
        }

        if (mode === 'progressive_discount') {
            return this.getProgressiveLevels().reduce((maxPercent, level) => {
                return Math.max(maxPercent, Number(level?.percentValue || 0));
            }, 0);
        }

        return 0;
    }

    getProgressiveLevels() {
        const levels = Array.isArray(this.config.progressiveLevels) ? this.config.progressiveLevels : [];

        return levels
            .map((level) => ({
                thresholdCents: this.toCents(level?.threshold),
                label: (level?.label || '').trim()
            }))
            .filter((level) => level.thresholdCents > 0)
            .sort((a, b) => a.thresholdCents - b.thresholdCents)
            .filter((level, index, arr) => index === 0 || arr[index - 1].thresholdCents !== level.thresholdCents)
            .slice(0, 3)
            .map((level) => ({
                ...level,
                percentValue: this.extractPercentValue(level.label)
            }));
    }

    extractPercentValue(value) {
        if (typeof value !== 'string') return 0;

        const match = value.replace(',', '.').match(/(\d+(?:\.\d+)?)\s*%/);
        if (!match) return 0;

        const percent = parseFloat(match[1]);
        return Number.isFinite(percent) ? percent : 0;
    }

    getItemDiscountPercent(item) {
        const originalLinePrice = Number(item?.original_line_price);
        const finalLinePrice = Number(item?.final_line_price);

        if (!Number.isFinite(originalLinePrice) || originalLinePrice <= 0 || !Number.isFinite(finalLinePrice) || finalLinePrice < 0 || finalLinePrice >= originalLinePrice) {
            return 0;
        }

        return ((originalLinePrice - finalLinePrice) / originalLinePrice) * 100;
    }

    getActiveLineDiscountPercent(cart) {
        const items = Array.isArray(cart?.items) ? cart.items : [];

        return items.reduce((maxPercent, item) => {
            if (this.isShippingDeliveryMode() && item?.gift_card) {
                return maxPercent;
            }

            return Math.max(maxPercent, this.getItemDiscountPercent(item));
        }, 0);
    }

    getDiscountApplicationPercent(application) {
        if (!application) return 0;

        const applicationType = String(application.type || '').toLowerCase();
        const applicationValue = Number(application.value);

        if (applicationType === 'percentage' && Number.isFinite(applicationValue) && applicationValue > 0) {
            return applicationValue;
        }

        const textPercent = this.extractPercentValue([
            application.title,
            application.description,
            application.code
        ].filter(Boolean).join(' '));

        if (textPercent > 0) {
            return textPercent;
        }

        return 0;
    }

    getCartLevelDiscountPercent(cart) {
        const discountApplications = []
            .concat(Array.isArray(cart?.cart_level_discount_applications) ? cart.cart_level_discount_applications : [])
            .concat(Array.isArray(cart?.discount_applications) ? cart.discount_applications : []);

        return discountApplications.reduce((maxPercent, application) => {
            return Math.max(maxPercent, this.getDiscountApplicationPercent(application));
        }, 0);
    }

    getAppliedDiscountPercent(cart) {
        return Math.max(
            this.getActiveLineDiscountPercent(cart),
            this.getCartLevelDiscountPercent(cart)
        );
    }

    getClassLabel(progressPercent) {
        if (progressPercent <= 0) {
            return 'none';
        }

        if (progressPercent <= 30) {
            return FreeShippingMeter.classLabel1;
        }

        if (progressPercent <= 60) {
            return FreeShippingMeter.classLabel2;
        }

        if (progressPercent < 100) {
            return FreeShippingMeter.classLabel3;
        }

        return 'progress-free';
    }

    resetProgressClass(classLabel) {
        this.progressBar.classList.remove('progress-30', 'progress-60', 'progress-100', 'progress-free');

        if (classLabel !== 'none') {
            this.progressBar.classList.add(classLabel);
        }
    }

    buildFreeShippingState(totalCents) {
        const thresholdCents = this.getFreeShippingThreshold();
        const texts = this.getFreeShippingTexts();

        if (thresholdCents <= 0) return null;

        const progressPercent = Math.min((totalCents / thresholdCents) * 100, 100);

        if (totalCents === 0) {
            return {
                showProgress: false,
                percent: 0,
                message: `<span>${texts.free_shipping_message || ''} ${this.formatMoney(thresholdCents)}!</span>`,
                messageClass: 'none'
            };
        }

        if (totalCents >= thresholdCents) {
            const classLabel = this.getClassLabel(100);
            return {
                showProgress: true,
                percent: 100,
                message: texts.free_shipping_message_1 || '',
                messageClass: classLabel
            };
        }

        const remainingCents = thresholdCents - totalCents;
        const classLabel = this.getClassLabel(progressPercent);

        return {
            showProgress: true,
            percent: progressPercent,
            message: `<span>${texts.free_shipping_message_2 || ''} </span>${this.formatMoney(remainingCents)}<span> ${texts.free_shipping_message_3 || ''} </span><span class="text">${texts.free_shipping_message_4 || ''}</span>`,
            messageClass: classLabel
        };
    }

    buildSingleDiscountState(totalCents) {
        const thresholdCents = this.getSingleDiscountThreshold();
        const reward = this.config.discountReward || '';

        if (thresholdCents <= 0) return null;

        const progressPercent = Math.min((totalCents / thresholdCents) * 100, 100);
        const thresholdFormatted = this.formatMoney(thresholdCents);

        if (totalCents >= thresholdCents) {
            const classLabel = this.getClassLabel(100);
            return {
                showProgress: true,
                percent: 100,
                message: this.formatTemplate(this.config.discountSuccessTemplate, {
                    reward,
                    threshold: thresholdFormatted
                }),
                messageClass: classLabel
            };
        }

        const remainingCents = Math.max(0, thresholdCents - totalCents);
        const classLabel = this.getClassLabel(progressPercent);

        return {
            showProgress: true,
            percent: progressPercent,
            message: this.formatTemplate(this.config.discountRemainingTemplate, {
                amount: this.formatMoney(remainingCents),
                reward,
                threshold: thresholdFormatted
            }),
            messageClass: classLabel
        };
    }

    buildProgressiveState(totalCents, cart) {
        const levels = this.getProgressiveLevels();

        if (!levels.length) return null;

        const maxThresholdCents = levels[levels.length - 1].thresholdCents;
        const subtotalLevelIndex = levels.reduce((matchedIndex, level, index) => (
            totalCents >= level.thresholdCents ? index : matchedIndex
        ), -1);
        const activeDiscountPercent = this.getActiveLineDiscountPercent(cart);
        const discountLevelIndex = levels.reduce((matchedIndex, level, index) => (
            level.percentValue > 0 && activeDiscountPercent + 0.01 >= level.percentValue ? index : matchedIndex
        ), -1);
        const achievedLevelIndex = Math.max(subtotalLevelIndex, discountLevelIndex);
        const effectiveProgressCents = achievedLevelIndex >= 0
            ? Math.max(totalCents, levels[achievedLevelIndex].thresholdCents)
            : totalCents;
        const progressPercent = Math.min((effectiveProgressCents / maxThresholdCents) * 100, 100);
        const nextLevel = achievedLevelIndex >= levels.length - 1 ? null : levels[achievedLevelIndex + 1];
        const activeLevel = achievedLevelIndex >= 0 ? levels[achievedLevelIndex] : null;

        if (!nextLevel) {
            const classLabel = this.getClassLabel(100);
            const finalLevel = activeLevel || levels[levels.length - 1];
            const activeThresholdFormatted = this.formatMoney(finalLevel.thresholdCents);
            return {
                showProgress: true,
                percent: 100,
                message: this.formatTemplate(this.config.progressiveSuccessTemplate, {
                    reward: finalLevel.label || activeThresholdFormatted,
                    threshold: activeThresholdFormatted
                }),
                messageClass: classLabel
            };
        }

        const classLabel = this.getClassLabel(progressPercent);
        const nextThresholdFormatted = this.formatMoney(nextLevel.thresholdCents);

        return {
            showProgress: true,
            percent: progressPercent,
            message: this.formatTemplate(this.config.progressiveRemainingTemplate, {
                amount: this.formatMoney(Math.max(0, nextLevel.thresholdCents - totalCents)),
                reward: nextLevel.label || nextThresholdFormatted,
                threshold: nextThresholdFormatted
            }),
            messageClass: classLabel
        };
    }

    buildState(totalCents, cart) {
        const mode = this.getMode();

        if (mode === 'discount') {
            return this.buildSingleDiscountState(totalCents) || this.buildFreeShippingState(totalCents);
        }

        if (mode === 'progressive_discount') {
            return this.buildProgressiveState(totalCents, cart) || this.buildFreeShippingState(totalCents);
        }

        return this.buildFreeShippingState(totalCents);
    }

    render(cart) {
        if (!this.progressBar || !this.progressMeter || !this.messageElement) return;

        const subtotalCents = this.getEligibleSubtotal(cart);
        const maxConfiguredDiscountPercent = this.getConfiguredMaxDiscountPercent();
        const activeDiscountPercent = this.getAppliedDiscountPercent(cart);

        if (maxConfiguredDiscountPercent > 0 && activeDiscountPercent > maxConfiguredDiscountPercent + 0.01) {
            const fallbackState = this.buildFreeShippingState(subtotalCents);

            if (!fallbackState) {
                this.style.display = 'none';
                this.setAttribute('hidden', 'hidden');
                return;
            }

            this.style.display = 'block';
            this.removeAttribute('hidden');

            const fallbackPercent = Math.max(0, Math.min(fallbackState.percent || 0, 100));
            const fallbackClassLabel = this.getClassLabel(fallbackPercent);

            this.resetProgressClass(fallbackClassLabel);

            if (fallbackState.showProgress) {
                this.progressBar.classList.remove('progress-hidden');
            } else {
                this.progressBar.classList.add('progress-hidden');
            }

            this.progressTrack?.setAttribute('aria-valuenow', fallbackPercent.toFixed(2));
            this.messageElement.innerHTML = fallbackState.message || '';
            this.messageElement.classList.remove('progress-30', 'progress-60', 'progress-100', 'progress-free');
            if (fallbackState.messageClass) {
                this.messageElement.classList.add(fallbackState.messageClass);
            }

            requestAnimationFrame(() => {
                this.progressMeter.style.width = `${fallbackPercent}%`;

                if (this.textEnabled) {
                    const textNode = this.progressMeter.querySelector('.text');
                    if (textNode) {
                        textNode.textContent = `${fallbackPercent.toFixed(0)}%`;
                    }
                }
            });

            return;
        }

        this.style.display = 'block';
        this.removeAttribute('hidden');
        const state = this.buildState(subtotalCents, cart);

        if (!state) {
            this.progressBar.classList.add('progress-hidden');
            this.messageElement.innerHTML = '';
            this.messageElement.classList.remove('progress-30', 'progress-60', 'progress-100', 'progress-free');
            return;
        }

        const progressPercent = Math.max(0, Math.min(state.percent || 0, 100));
        const classLabel = this.getClassLabel(progressPercent);

        this.resetProgressClass(classLabel);

        if (state.showProgress) {
            this.progressBar.classList.remove('progress-hidden');
        } else {
            this.progressBar.classList.add('progress-hidden');
        }

        this.progressTrack?.setAttribute('aria-valuenow', progressPercent.toFixed(2));
        this.messageElement.innerHTML = state.message || '';
        this.messageElement.classList.remove('progress-30', 'progress-60', 'progress-100', 'progress-free');
        if (state.messageClass) {
            this.messageElement.classList.add(state.messageClass);
        }

        requestAnimationFrame(() => {
            this.progressMeter.style.width = `${progressPercent}%`;

            if (this.textEnabled) {
                const textNode = this.progressMeter.querySelector('.text');
                if (textNode) {
                    textNode.textContent = `${progressPercent.toFixed(0)}%`;
                }
            }
        });
    }
}

if (!customElements.get('free-shipping-component')) {
    customElements.define('free-shipping-component', FreeShippingMeter);
}
